// GtBenches — benches: named shortlists of people kept warm for the next hire.
//
// A bench is not a role. It never contacts anyone: nothing here emails, changes
// what a candidate sees, or creates a submission. Buckets are Jimmy's own and a
// new bench starts with none.
//
// The bench page is built around its buckets. They run across the top and
// scroll sideways when there are more than fit. Clicking one shows everyone in
// it as a table that each bench shapes for itself: standard columns can be
// shown, hidden and reordered, and the bench can carry columns of its own
// (text, number, date, choice, yes/no, link) filled in right in the cell.
// People move between buckets from a row, in bulk, or by dragging a row onto a
// bucket. "Move to role" puts them on a live role through the same move step as
// everywhere else; they stay on the bench.
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API_BASE from '../../config';
import GtLoader from '../../components/GtLoader';
import { GtSelect, GtDatePicker } from '../../components/GtControls';
import { gtConfirm } from '../../components/GtConfirm';
import { ClientField, ExpertiseField } from '../../components/GtTaxonomyFields';
import GtNoticeModal from '../../components/GtNoticeModal';
import GtBulkNoticeModal from '../../components/GtBulkNoticeModal';
import GtSendFormModal from '../../components/GtSendFormModal';
import GtEmailModal from '../../components/GtEmailModal'; // C1-EMAIL
import GtSequenceStart from '../../components/GtSequenceStart'; // C1-SEQ2
import { TempDot, tempOf, tempCounts, BenchTempTrend } from '../../components/gtTemp'; // C1-TEMPHIST
import GtCandidateRecord from './GtCandidateRecord';
import GtImportWizard from '../../components/GtImportWizard';
import { downloadDossier } from '../../components/gtDossier';
import GtDossierView from '../../components/GtDossierView';
import GtAiColumnRun from '../../components/GtAiColumnRun';
import { GtEnrichModal, GtEnrichProgress } from '../../components/GtEnrich';
import GtSourcingPanel from '../../components/GtSourcing';
import GtInterviewPlan from '../../components/GtInterviewPlan';
// C1-BENCHX: activity, imports history, undo, duplicates and shortlist links.
import { GtBenchActivity, GtBenchDuplicates, GtBenchClientPick, findDuplicates } from '../../components/GtBenchExtras';
import { MergeModal } from '../../components/GtCandidateBulk';
import GtShareCreate from './GtShareCreate';
import { useDeepMatch, searchPool, SearchMatchLine } from '../../components/gtSearch'; // C1-SEARCH

const TONE_HEX = {
  teal: '#17838A', slate: '#64748B', blue: '#1565C0', purple: '#6A1B9A',
  amber: '#B26B00', green: '#056F4E', rose: '#BE123C',
};
const TONES = Object.keys(TONE_HEX);
// Buckets are colour-filled cards. Every stored tone maps into one family of
// teals, light greens and light blues, so older buckets match too.
const BUCKET = {
  teal:   { bg: '#D8F1F2', fg: '#0E6469', strong: '#17838A', name: 'Teal' },
  slate:  { bg: '#C9E7E6', fg: '#0B4F53', strong: '#0F6468', name: 'Deep teal' },
  purple: { bg: '#D6EFF6', fg: '#0C6378', strong: '#1A86A1', name: 'Aqua' },
  green:  { bg: '#DFF3E5', fg: '#1D6F40', strong: '#2E8F55', name: 'Light green' },
  blue:   { bg: '#DDEBFA', fg: '#1B5A94', strong: '#2D74BA', name: 'Light blue' },
  amber:  { bg: '#E3F5EE', fg: '#176B52', strong: '#22896A', name: 'Mint' },
  rose:   { bg: '#E5F1FC', fg: '#285F8F', strong: '#3A7FBE', name: 'Sky' },
};
const BUCKET_TONES = ['teal', 'green', 'blue', 'slate', 'purple'];
function bucketColour(tone) { return BUCKET[tone] || BUCKET.teal; }
const ALL = '__all';
const NONE = '__none';
const SUGGESTED = '__suggested';
const INTERVIEW = '__interview';
const PAGE_SIZES = [25, 50, 100];
const FILTERS = [
  { key: 'onRole', label: 'On a role', test: m => (m.roles || []).length > 0 },
  { key: 'notOnRole', label: 'Not on any role', test: m => !(m.roles || []).length },
  { key: 'stale', label: 'Not touched in 30 days', test: m => staleDays(m.last_touched_at) >= 30 },
  { key: 'hasEmail', label: 'Has an email', test: m => !!m.email },
  { key: 'hasLinkedin', label: 'Has a LinkedIn', test: m => !!m.linkedin_url },
];
function csvCell(v) {
  const t = v == null ? '' : String(v);
  return /[",\n]/.test(t) ? '"' + t.replace(/"/g, '""') + '"' : t;
}

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'select', label: 'Choice list' },
  { value: 'yesno', label: 'Yes or no' },
  { value: 'url', label: 'Link' },
  { value: 'ai', label: 'AI question' },
];
const AI_ANSWER = [{ value: 'yesno', label: 'Yes, no or unknown' }, { value: 'short', label: 'Short answer' }];
const UNIT = { hour: '/hr', day: '/day', week: '/wk', month: '/mo', year: '/yr' };
const RTW = {
  citizen: 'Citizen', permanent_resident: 'Permanent resident', visa_holder: 'Visa holder',
  needs_sponsorship: 'Needs sponsorship', unknown: 'Unknown',
};
const SOURCE = {
  manual: 'Added by hand', candidates_table: 'Candidates table', record: 'Candidate record',
  role: 'From a role', sourcing: 'AI sourcing', import: 'Import',
};

function initials(name) { return String(name || '?').trim().split(/\s+/).map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || '?'; }
const AVC = ['#1E5A7B','#2E7D32','#6A1B9A','#B8860B','#00695C','#C2410C','#374151','#9D174D','#1565C0','#4E342E','#5B6C1E','#0F766E','#B91C1C','#5D4037','#3949AB','#00838F'];
function avatarColour(name) { const s = String(name || '?'); let h = 0; for (let i = 0; i < s.length; i++) h = ((h * 31) + s.charCodeAt(i)) >>> 0; return AVC[h % AVC.length]; }
function staleDays(iso) { return iso ? Math.floor((Date.now() - new Date(iso)) / 86400000) : 0; }
function niceDate(v) {
  if (!v) return '';
  const d = new Date(String(v).length <= 10 ? String(v) + 'T00:00:00' : v);
  return isNaN(d) ? String(v) : d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
}
function words(v) { return v ? String(v).replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase()) : ''; }
function money(n, unit) {
  if (n == null || n === '') return '';
  return '£' + Number(n).toLocaleString('en-GB', { maximumFractionDigits: 0 }) + (UNIT[unit] || '');
}

// The standard columns. `value` is what sorting and search use; `cell` draws it.
const STD = [
  { key: 'name', label: 'Name', locked: true, width: 390, value: m => m.full_name },
  { key: 'bucket', label: 'Bucket', width: 150 },
  { key: 'current_title', label: 'Title', width: 200, value: m => m.current_title },
  { key: 'current_company', label: 'Company', width: 170, value: m => m.current_company },
  { key: 'headline', label: 'Headline', width: 240, value: m => m.headline },
  { key: 'location', label: 'Location', width: 170, value: m => m.location },
  { key: 'expertise_domain', label: 'Expertise', width: 150, value: m => words(m.expertise_domain) },
  { key: 'availability_date', label: 'Available from', width: 140, value: m => m.availability_date, cell: m => niceDate(m.availability_date) },
  { key: 'notice_period', label: 'Notice', width: 120, value: m => m.notice_period },
  { key: 'right_to_work', label: 'Right to work', width: 170,
    value: m => [RTW[m.right_to_work_status] || words(m.right_to_work_status), m.right_to_work_country].filter(Boolean).join(' · ') },
  { key: 'expected_rate', label: 'Rate', width: 130, value: m => (m.expected_rate_gbp == null ? null : Number(m.expected_rate_gbp)),
    cell: m => money(m.expected_rate_gbp, m.expected_rate_unit) },
  { key: 'email', label: 'Email', width: 220, value: m => m.email,
    cell: m => (m.email ? <a href={'mailto:' + m.email} onClick={e => e.stopPropagation()}>{m.email}</a> : '') },
  { key: 'phone', label: 'Phone', width: 150, value: m => m.phone },
  { key: 'linkedin_url', label: 'LinkedIn', width: 110, value: m => m.linkedin_url,
    cell: m => (m.linkedin_url ? <a href={m.linkedin_url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}>Profile ↗</a> : '') },
  { key: 'github_username', label: 'GitHub', width: 130, value: m => m.github_username,
    cell: m => (m.github_username ? <a href={'https://github.com/' + m.github_username} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}>{m.github_username}</a> : '') },
  { key: 'status', label: 'Candidate status', width: 150, value: m => words(m.candidate_status) },
  { key: 'roles', label: 'On roles', width: 290, value: m => (m.roles || []).length },
  { key: 'added_at', label: 'Added', width: 95, value: m => m.added_at, cell: m => niceDate(m.added_at) },
  { key: 'last_touched_at', label: 'Last touched', width: 105, value: m => m.last_touched_at },
  { key: 'source', label: 'Added from', width: 160, value: m => SOURCE[m.source] || m.source },
  { key: 'added_by_name', label: 'Added by', width: 150, value: m => m.added_by_name },
];
const STD_BY_KEY = {};
STD.forEach(c => { STD_BY_KEY[c.key] = c; });
const DEFAULT_COLUMNS = ['name', 'bucket', 'current_title', 'current_company', 'location', 'roles', 'added_at', 'last_touched_at'];

export default function GtBenches({ getToken, userName, userImage }) {
  const { benchId, candidateId } = useParams();
  const navigate = useNavigate();
  const [toast, setToast] = useState(null);

  const authFetch = useCallback(async (path, opts = {}) => {
    const token = await getToken();
    const headers = { ...(opts.headers || {}), Authorization: 'Bearer ' + token };
    if (opts.body && !headers['Content-Type']) headers['Content-Type'] = 'application/json';
    const res = await fetch(API_BASE + path, { ...opts, headers });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = new Error(body.message || body.detail || body.error || ('request failed: ' + res.status));
      err.status = res.status; err.body = body;
      throw err;
    }
    return body;
  }, [getToken]);

  const toastSeq = useRef(0);
  const showToast = useCallback((t, ms) => {
    toastSeq.current += 1;
    const n = toastSeq.current;
    setToast(t);
    setTimeout(() => { if (toastSeq.current === n) setToast(null); }, ms);
  }, []);
  const flash = useCallback((msg, isErr) => showToast({ msg, err: !!isErr }, isErr ? 7000 : 3500), [showToast]);
  const flashUndo = useCallback((msg, undo) => showToast({ msg, undo }, 10000), [showToast]);
  function runUndo() {
    const u = toast && toast.undo;
    toastSeq.current += 1;
    setToast(null);
    if (u) u();
  }

  let body;
  if (benchId && candidateId) {
    body = (
      <GtCandidateRecord candidateId={candidateId} getToken={getToken} userName={userName} userImage={userImage}
        backLabel="Bench" onBack={() => navigate('/benches/' + benchId)} />
    );
  } else if (benchId) {
    body = <BenchRecord benchKey={benchId} authFetch={authFetch} flash={flash} flashUndo={flashUndo} navigate={navigate} getToken={getToken} />;
  } else {
    body = <BenchList authFetch={authFetch} flash={flash} navigate={navigate} />;
  }

  return (
    <>
      {body}
      {toast && (
        <div className={'gtc-toast' + (toast.err ? ' gtc-toast--err' : '') + (toast.undo ? ' gtbx-toast' : '')} role="status">
          <span>{toast.msg}</span>
          {toast.undo && <button type="button" className="gtbx-undo" onClick={runUndo}>Undo</button>}
        </div>
      )}
    </>
  );
}

// ── The list of benches ───────────────────────────────────────────────────
function BenchList({ authFetch, flash, navigate }) {
  const [status, setStatus] = useState('active');
  const [benches, setBenches] = useState(null);
  const [q, setQ] = useState('');
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await authFetch('/api/benches?status=' + status);
      setBenches(r.benches || []);
    } catch (e) { setBenches([]); flash(e.message, true); }
  }, [authFetch, flash, status]);

  useEffect(() => { setBenches(null); load(); }, [load]);

  const ql = q.trim().toLowerCase();
  const shown = (benches || []).filter(b => !ql
    || [b.name, b.client_name, b.brief, b.location, b.ref].join(' ').toLowerCase().indexOf(ql) !== -1);

  return (
    <div className="gtbn-page">
      <div className="gtbn-top">
        <div>
          <h1 className="gtc-title">Benches</h1>
          <p className="gtc-sub">People you are keeping warm for the next hire. A bench never contacts anyone.</p>
        </div>
        <button className="gtc-primary" type="button" onClick={() => setCreating(true)}>New bench</button>
      </div>

      <div className="gtbn-toolbar">
        <input className="gtsb-search" placeholder="Search benches…" value={q} onChange={e => setQ(e.target.value)} />
        <div className="gtpl-chips">
          <button className={'gtsb-chip' + (status === 'active' ? ' gtsb-chip--on' : '')} onClick={() => setStatus('active')}>Active</button>
          <button className={'gtsb-chip' + (status === 'archived' ? ' gtsb-chip--on' : '')} onClick={() => setStatus('archived')}>Archived</button>
        </div>
      </div>

      {benches === null ? <GtLoader compact /> : benches.length === 0 ? (
        <div className="gtsb-empty">
          <div className="gtsb-empty-mark">◆</div>
          <h3>{status === 'archived' ? 'No archived benches' : 'No benches yet'}</h3>
          <p>{status === 'archived'
            ? 'Benches you archive land here. They keep their people and columns.'
            : 'Start one for a client planning to hire, or for a market you want to map. Create your own buckets, add people to them, and move them onto a role when it opens.'}</p>
          {status === 'active' && <button className="gtc-primary" onClick={() => setCreating(true)}>New bench</button>}
        </div>
      ) : shown.length === 0 ? (
        <div className="gtr-emptytab">No benches match that search.</div>
      ) : (
        <div className="gtbn-grid">
          {shown.map((b, i) => (
            <button type="button" key={b.id} className={'gtbn-card gtbn-card--' + TONES[i % TONES.length]}
              onClick={() => navigate('/benches/' + b.ref)}>
              <div className="gtbn-card-top">
                <span className="gtbn-card-name">{b.name}</span>
                <span className="gtr-refbadge gtr-refbadge-sm">{b.ref}</span>
              </div>
              <div className="gtbn-card-client">{b.client_name ? b.client_name : 'No client · market map'}</div>
              {b.brief && <div className="gtbn-card-brief">{b.brief}</div>}
              <div className="gtbn-card-stats">
                <span><b>{b.member_count}</b> {b.member_count === 1 ? 'person' : 'people'}</span>
              </div>
              <div className="gtbn-card-foot">
                <span className={staleDays(b.last_touched_at || b.updated_at) >= 30 ? 'gtbn-stale' : ''}>Touched {niceDate(b.last_touched_at || b.updated_at)}</span>
                {b.owner_name && <span>{b.owner_name}</span>}
              </div>
            </button>
          ))}
        </div>
      )}

      {creating && (
        <BenchForm authFetch={authFetch} flash={flash} onClose={() => setCreating(false)}
          onSaved={(bench) => { setCreating(false); navigate('/benches/' + bench.ref); }} />
      )}
    </div>
  );
}

// ── New or edit bench ────────────────────────────────────────────────────
function BenchForm({ bench, authFetch, flash, onClose, onSaved }) {
  const editing = !!bench;
  const [f, setF] = useState({
    name: bench ? bench.name : '',
    client_id: bench && bench.client_id ? bench.client_id : '',
    expertise_domain: bench ? bench.expertise_domain || '' : '',
    location: bench ? bench.location || '' : '',
    brief: bench ? bench.brief || '' : '',
    copy_buckets_from: '',
  });
  const [others, setOthers] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (editing) return;
    authFetch('/api/benches?status=all')
      .then(r => setOthers((r.benches || []).filter(b => b.bucket_count > 0)))
      .catch(() => setOthers([]));
  }, [authFetch, editing]);

  function set(k, v) { setF(prev => ({ ...prev, [k]: v })); }

  async function save() {
    if (!f.name.trim()) { flash('Give the bench a name.', true); return; }
    setBusy(true);
    try {
      const payload = {
        name: f.name, client_id: f.client_id || null, expertise_domain: f.expertise_domain || null,
        location: f.location, brief: f.brief,
      };
      if (!editing && f.copy_buckets_from) payload.copy_buckets_from = f.copy_buckets_from;
      const r = await authFetch(editing ? '/api/benches/' + bench.id : '/api/benches', {
        method: editing ? 'PATCH' : 'POST', body: JSON.stringify(payload),
      });
      flash(editing ? 'Saved' : 'Bench created');
      onSaved(r.bench);
    } catch (e) { flash(e.message, true); }
    setBusy(false);
  }

  return (
    <div className="gtc-modal-backdrop" onClick={() => !busy && onClose()}>
      <div className="gtc-modal" onClick={e => e.stopPropagation()}>
        <div className="gtc-modal-head">{editing ? 'Edit bench' : 'New bench'}</div>
        <div className="gtc-modal-body">
          <label className="gtc-field"><span>Name</span>
            <input autoFocus value={f.name} maxLength={120} placeholder="e.g. Eval engineers for Q1"
              onChange={e => set('name', e.target.value)} />
          </label>
          <div className="gtc-field"><span>Client <em className="gtbn-opt">optional</em></span>
            <div className="gtbn-row">
              <div className="gtbn-grow"><ClientField value={f.client_id} onChange={v => set('client_id', v)} authFetch={authFetch} flash={flash} placeholder="No client (market map)" /></div>
              {f.client_id ? <button type="button" className="gtc-btn-ghost" onClick={() => set('client_id', '')}>Clear</button> : null}
            </div>
          </div>
          <div className="gtc-field"><span>Expertise <em className="gtbn-opt">optional</em></span>
            <ExpertiseField value={f.expertise_domain} onChange={v => set('expertise_domain', v)} authFetch={authFetch} flash={flash} />
          </div>
          <label className="gtc-field"><span>Location <em className="gtbn-opt">optional</em></span>
            <input value={f.location} maxLength={255} placeholder="e.g. London or remote UK" onChange={e => set('location', e.target.value)} />
          </label>
          <label className="gtc-field"><span>Brief <em className="gtbn-opt">optional</em></span>
            <textarea rows={4} value={f.brief} placeholder="Who you are looking for and why. Plain words are fine."
              onChange={e => set('brief', e.target.value)} />
          </label>
          {!editing && others.length > 0 && (
            <div className="gtc-field"><span>Copy buckets from <em className="gtbn-opt">optional</em></span>
              <GtSelect value={f.copy_buckets_from} onChange={v => set('copy_buckets_from', v)} placeholder="Start with no buckets"
                options={[{ value: '', label: 'Start with no buckets' }].concat(others.map(b => ({ value: b.id, label: b.name + ' (' + b.bucket_count + ')' })))} />
            </div>
          )}
        </div>
        <div className="gtc-modal-foot">
          <button className="gtc-btn-ghost" disabled={busy} onClick={onClose}>Cancel</button>
          <button className="gtc-primary" disabled={busy || !f.name.trim()} onClick={save}>{busy ? 'Saving…' : editing ? 'Save' : 'Create bench'}</button>
        </div>
      </div>
    </div>
  );
}

// ── One bench ─────────────────────────────────────────────────────────────
function BenchRecord({ benchKey, authFetch, flash, flashUndo, navigate, getToken }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [active, setActive] = useState(ALL);
  const [q, setQ] = useState('');
  const [sort, setSort] = useState({ key: 'last_touched_at', dir: 'desc' });
  const [selected, setSelected] = useState([]);
  const [bulkBucket, setBulkBucket] = useState('');
  const [formFor, setFormFor] = useState(null);
  const [emailFor, setEmailFor] = useState(null); // C1-EMAIL
  const [seqFor, setSeqFor] = useState(null); // C1-SEQ2
  const [busy, setBusy] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [filters, setFilters] = useState([]);
  const [temps, setTemps] = useState([]); // C1-BENCHTEMP: show only these temperatures
  const [dragIds, setDragIds] = useState(null);
  const [dropOn, setDropOn] = useState(null);
  const [newBucket, setNewBucket] = useState(null);
  const [editing, setEditing] = useState(false);
  const [managing, setManaging] = useState(false);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [toRole, setToRole] = useState(null);
  const [aiRun, setAiRun] = useState(null);
  const [editCell, setEditCell] = useState(null);
  const [suggestedN, setSuggestedN] = useState(0);
  const [activityTab, setActivityTab] = useState(null);
  const [dupOpen, setDupOpen] = useState(false);
  const [mergeIds, setMergeIds] = useState(null);
  const [share, setShare] = useState(null);
  const pendingSelect = useRef(null);
  const barRef = useRef(null);
  const tableRef = useRef(null);
  const [edges, setEdges] = useState({ left: false, right: false });
  const [overflow, setOverflow] = useState({ left: false, right: false });

  const load = useCallback(async () => {
    try {
      const r = await authFetch('/api/benches/' + benchKey);
      setData(r);
      setSelected(sel => sel.filter(id => (r.members || []).some(m => m.id === id)));
    } catch (e) { setError(e.status === 404 ? 'This bench could not be found.' : e.message); }
  }, [authFetch, benchKey]);

  useEffect(() => { load(); }, [load]);
  // How many suggestions are waiting, for the Suggested tab.
  const benchDbId = data && data.bench ? data.bench.id : null;
  const loadSuggestedN = useCallback(() => {
    if (!benchDbId) return;
    authFetch('/api/sourcing/bench/' + benchDbId).then(r => setSuggestedN((r.summary && r.summary.new) || 0)).catch(() => {});
  }, [authFetch, benchDbId]);
  useEffect(() => { loadSuggestedN(); }, [loadSuggestedN]);
  const onSourcingChanged = useCallback(() => { load(); loadSuggestedN(); }, [load, loadSuggestedN]);
  useEffect(() => { setSelected(pendingSelect.current || []); pendingSelect.current = null; if (tableRef.current) tableRef.current.scrollLeft = 0; }, [active]);
  useEffect(() => { setPage(0); }, [active, q, filters, temps, sort, pageSize]);

  const measureTable = useCallback(() => {
    const el = tableRef.current;
    if (!el) return;
    setEdges({ left: el.scrollLeft > 2, right: el.scrollLeft + el.clientWidth < el.scrollWidth - 2 });
  }, []);
  useEffect(() => {
    measureTable();
    window.addEventListener('resize', measureTable);
    return () => window.removeEventListener('resize', measureTable);
  }, [measureTable, data, active]);

  const measure = useCallback(() => {
    const el = barRef.current;
    if (!el) return;
    setOverflow({ left: el.scrollLeft > 4, right: el.scrollLeft + el.clientWidth < el.scrollWidth - 4 });
  }, []);
  useEffect(() => {
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure, data]);

  const fields = useMemo(() => (data ? data.fields || [] : []), [data]);
  const columns = useMemo(() => {
    if (!data) return [];
    const saved = Array.isArray(data.bench.columns) && data.bench.columns.length ? data.bench.columns : DEFAULT_COLUMNS;
    const fieldKeys = fields.map(f => 'f:' + f.key);
    return saved.filter(k => STD_BY_KEY[k] || fieldKeys.indexOf(k) !== -1);
  }, [data, fields]);

  // Enrichment runs for people on this bench, picked up again after a reload.
  const [enrichFor, setEnrichFor] = useState(null);
  const [dossierFor, setDossierFor] = useState(null);
  const [enrichRuns, setEnrichRuns] = useState([]);
  const enrichChecked = useRef('');
  useEffect(() => {
    const ids = data && data.members ? data.members.map(m => m.candidate_id) : [];
    const key = ids.join(',');
    if (!key || enrichChecked.current === key) return;
    enrichChecked.current = key;
    authFetch('/api/enrich/status?candidate_ids=' + key).then(r => {
      const live = (r.runs || []).filter(x => x.status === 'queued' || x.status === 'running').map(x => x.id);
      if (live.length) setEnrichRuns(cur => (cur.length ? cur : live));
    }).catch(() => {});
  }, [data, authFetch]);
  const onEnrichFinished = useCallback(() => { load(); }, [load]);
  // C1-SEARCH: the box also finds people by anything in their record (CV, skills, notes...).
  const deep = useDeepMatch(authFetch, q, data && data.members ? data.members.map(m => m.candidate_id) : []);

  if (error) {
    return <div className="gtbn-page"><button className="gtr-back" onClick={() => navigate('/benches')}>← Benches</button><div className="gtc-error">{error}</div></div>;
  }
  if (!data) return <div className="gtbn-page"><GtLoader /></div>;

  const { bench, buckets, members } = data;
  const archived = bench.status === 'archived';
  const bucketById = {};
  buckets.forEach(b => { bucketById[b.id] = b; });
  const fieldByKey = {};
  fields.forEach(f => { fieldByKey[f.key] = f; });
  const noBucket = members.filter(m => m.stage_id == null);
  const countIn = id => members.filter(m => m.stage_id === id).length;
  const dupGroups = findDuplicates(members);

  // Who is in view.
  const inBucket = active === ALL ? members
    : active === NONE ? noBucket
    : members.filter(m => m.stage_id === active);
  const ql = q.trim().toLowerCase();
  // Each yes/no AI column adds its own filters.
  const aiFilters = [];
  fields.filter(f => f.type === 'ai' && (!f.options || f.options.answer !== 'short')).forEach(f => {
    [['yes', 'Yes'], ['no', 'No'], ['unknown', 'Unknown']].forEach(([a, l]) => aiFilters.push({
      key: 'ai:' + f.key + ':' + a, label: f.label + ': ' + l,
      test: m => { const v = (m.custom_values || {})[f.key]; return !!v && typeof v === 'object' && v.a === a; },
    }));
  });
  const allFilters = FILTERS.concat(aiFilters);
  const activeFilters = allFilters.filter(f => filters.indexOf(f.key) !== -1);
  const filtered = inBucket.filter(m => activeFilters.every(f => f.test(m))
    && (!temps.length || temps.indexOf(tempOf(m).key) !== -1)); // C1-BENCHTEMP
  function colValue(key, m) {
    if (key === 'ref') return m.ref;
    if (key === 'bucket') return m.stage_id == null ? '' : (bucketById[m.stage_id] || {}).label;
    if (key.indexOf('f:') === 0) { const v = (m.custom_values || {})[key.slice(2)]; return v == null ? null : (typeof v === 'object' ? v.a : v); }
    const c = STD_BY_KEY[key];
    return c && c.value ? c.value(m) : null;
  }
  const pastedRefs = (q.match(/GTC-[A-Z]{1,2}[23456789ABCDEFGHJKMNPQRSTVWXYZ]{6}/gi) || []).map(r => r.toUpperCase());
  const searched = pastedRefs.length ? filtered.filter(m => pastedRefs.indexOf(String(m.ref || '').toUpperCase()) !== -1)
    : !ql ? filtered : filtered.filter(m => {
    const hay = [m.full_name, m.ref, m.current_title, m.current_company, m.headline, m.location, m.email]
      .concat(Object.values(m.custom_values || {}).map(v => (v && typeof v === 'object' ? v.a : v))).join(' ').toLowerCase();
    return hay.indexOf(ql) !== -1 || deep.ids.has(m.candidate_id);
  });
  const sorted = searched.slice().sort((a, b) => {
    const av = colValue(sort.key, a); const bv = colValue(sort.key, b);
    const ae = av == null || av === ''; const be = bv == null || bv === '';
    if (ae && be) return 0;
    if (ae) return 1;
    if (be) return -1;
    const r = typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av).localeCompare(String(bv), 'en-GB', { numeric: true });
    return sort.dir === 'asc' ? r : -r;
  });
  const pages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePage = Math.min(page, pages - 1);
  const shown = sorted.slice(safePage * pageSize, (safePage + 1) * pageSize);

  function exportCsv() {
    const rows = selected.length ? sorted.filter(m => selected.indexOf(m.id) !== -1) : sorted;
    const cols = ['ref'].concat(columns);
    const label = k => (k === 'ref' ? 'ID' : headerLabel(k));
    const text = (k, m) => {
      if (k === 'roles') return (m.roles || []).map(r => r.title + (r.stage ? ' (' + r.stage + ')' : '')).join('; ');
      if (k === 'added_at' || k === 'last_touched_at' || k === 'availability_date') return niceDate(m[k]);
      if (k === 'expected_rate') return money(m.expected_rate_gbp, m.expected_rate_unit);
      if (k.indexOf('f:') === 0) {
        const f = fieldByKey[k.slice(2)]; const v = (m.custom_values || {})[k.slice(2)];
        if (v == null) return '';
        if (f && f.type === 'ai') return typeof v === 'object' ? v.a + (v.e ? ' (' + v.e + ')' : '') : v;
        if (f && f.type === 'date') return niceDate(v);
        if (f && f.type === 'yesno') return v ? 'Yes' : 'No';
        return v;
      }
      return colValue(k, m);
    };
    const lines = [cols.map(k => csvCell(label(k))).join(',')]
      .concat(rows.map(m => cols.map(k => csvCell(text(k, m))).join(',')));
    const blob = new Blob(['\ufeff' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const day = new Date().toLocaleDateString('en-GB').split('/').reverse().join('-');
    a.href = url;
    a.download = (bench.name + ' - ' + activeLabel + ' - ' + day).replace(/[^\w\- ]+/g, '').trim() + '.csv';
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    flash(rows.length + (rows.length === 1 ? ' person' : ' people') + ' exported.');
  }
  const activeLabel = active === ALL ? 'All people' : active === NONE ? 'Not in a bucket' : (bucketById[active] || {}).label;

  const bucketOpts = buckets.map(b => ({ value: b.id, label: b.label }));

  async function moveTo(memberIds, stageId) {
    setBusy(true);
    try {
      const r = await authFetch('/api/benches/' + bench.id + '/members/move', {
        method: 'POST', body: JSON.stringify({ member_ids: memberIds, stage_id: stageId == null ? null : Number(stageId) }),
      });
      await load();
      const label = stageId == null ? 'out of their bucket' : 'to ' + (bucketById[stageId] || {}).label;
      const one = r.moved === 1 ? (members.find(m => m.id === (r.previous[0] || {}).id) || {}).full_name : null;
      const prev = r.previous || [];
      if (prev.length) flashUndo((one || r.moved + ' people') + ' moved ' + label + '.', () => undoMove(prev));
    } catch (e) { flash(e.message, true); }
    setBusy(false);
  }

  async function undoMove(prev) {
    const byStage = {};
    prev.forEach(p => { const k = p.stage_id == null ? 'none' : String(p.stage_id); (byStage[k] = byStage[k] || []).push(p.id); });
    try {
      for (const k of Object.keys(byStage)) {
        await authFetch('/api/benches/' + bench.id + '/members/move', {
          method: 'POST', body: JSON.stringify({ member_ids: byStage[k], stage_id: k === 'none' ? null : Number(k) }),
        });
      }
      await load(); flash('Moved back.');
    } catch (e) { await load(); flash(e.message, true); }
  }

  async function createBucket(label) {
    const name = String(label || '').trim();
    if (!name) { setNewBucket(null); return; }
    try {
      const r = await authFetch('/api/benches/' + bench.id + '/buckets', {
        method: 'PUT',
        body: JSON.stringify({ buckets: buckets.map(b => ({ id: b.id, label: b.label, tone: b.tone }))
          .concat([{ label: name, tone: BUCKET_TONES[buckets.length % BUCKET_TONES.length] }]) }),
      });
      setNewBucket(null);
      await load();
      const made = (r.buckets || []).find(b => b.label === name);
      if (made) setActive(made.id);
      setTimeout(() => { if (barRef.current) barRef.current.scrollLeft = barRef.current.scrollWidth; measure(); }, 50);
    } catch (e) { flash(e.message, true); }
  }

  async function removeMembers(ids) {
    const who = ids.length === 1 ? (members.find(m => m.id === ids[0]) || {}).full_name || '1 person' : null;
    try {
      const r = await authFetch('/api/benches/' + bench.id + '/members/remove', { method: 'POST', body: JSON.stringify({ member_ids: ids }) });
      setSelected([]); await load();
      const back = r.candidate_ids || [];
      const msg = (who && r.removed === 1 ? who : r.removed + (r.removed === 1 ? ' person' : ' people')) + ' taken off the bench.';
      if (back.length) flashUndo(msg, () => putBack(back)); else flash(msg);
    } catch (e) { flash(e.message, true); }
  }

  async function putBack(candidateIds) {
    try {
      const r = await authFetch('/api/benches/' + bench.id + '/members/restore', { method: 'POST', body: JSON.stringify({ candidate_ids: candidateIds }) });
      await load(); flash(r.restored + (r.restored === 1 ? ' person' : ' people') + ' put back.');
    } catch (e) { flash(e.message, true); }
  }

  // Send people to a client as a shortlist link. A bench with no client asks for one first.
  function startShare(list) {
    if (!list.length) return;
    const withdrawn = list.filter(m => m.consent_status === 'withdrawn').length;
    const ok = list.filter(m => m.consent_status !== 'withdrawn');
    if (withdrawn) flash(withdrawn + (withdrawn === 1 ? ' person has' : ' people have') + ' withdrawn consent and will be left out.', true);
    if (!ok.length) return;
    setShare({ people: ok, clientId: bench.client_id || null });
  }
  function shareList() { return selected.length ? members.filter(m => selected.indexOf(m.id) !== -1) : sorted; }

  async function saveCell(m, key, value) {
    try {
      await authFetch('/api/benches/' + bench.id + '/members/' + m.id, { method: 'PATCH', body: JSON.stringify({ custom: { [key]: value } }) });
      setEditCell(null);
      setData(d => ({
        ...d,
        members: d.members.map(x => {
          if (x.id !== m.id) return x;
          const cv = { ...(x.custom_values || {}) };
          if (value === null || value === '') delete cv[key]; else cv[key] = fieldByKey[key].type === 'number' ? Number(String(value).replace(/[, ]/g, '')) : value;
          return { ...x, custom_values: cv, last_touched_at: new Date().toISOString() };
        }),
      }));
    } catch (e) { flash(e.message, true); }
  }

  async function setStatus(next) {
    if (next === 'archived') {
      const ok = await gtConfirm({
        title: 'Archive this bench',
        message: 'It leaves the active list. Everyone stays on it, and you can restore it at any time.',
        confirmLabel: 'Archive',
      });
      if (!ok) return;
    }
    try {
      await authFetch('/api/benches/' + bench.id, { method: 'PATCH', body: JSON.stringify({ status: next }) });
      await load(); flash(next === 'archived' ? 'Archived' : 'Restored');
    } catch (e) { flash(e.message, true); }
  }

  async function deleteBench() {
    const ok = await gtConfirm({ title: 'Delete this bench', message: 'The bench, its buckets and its columns are removed for good.', confirmLabel: 'Delete', tone: 'danger' });
    if (!ok) return;
    try { await authFetch('/api/benches/' + bench.id, { method: 'DELETE' }); flash('Bench deleted'); navigate('/benches'); }
    catch (e) { flash(e.message, true); }
  }

  async function assignToMe() {
    try { await authFetch('/api/benches/' + bench.id, { method: 'PATCH', body: JSON.stringify({ owner: 'me' }) }); await load(); }
    catch (e) { flash(e.message, true); }
  }

  async function pdfFor(m) {
    flash('Making the PDF for ' + (m.full_name || m.ref) + '…');
    try { await downloadDossier(getToken, m.candidate_id, { name: m.full_name, refLabel: m.ref }); }
    catch (e) { flash(e.message, true); }
  }

  async function copyText(text, note) {
    try {
      if (navigator.clipboard && window.isSecureContext) await navigator.clipboard.writeText(text);
      else {
        const t = document.createElement('textarea');
        t.value = text; t.setAttribute('readonly', ''); t.style.position = 'fixed'; t.style.opacity = '0';
        document.body.appendChild(t); t.select(); document.execCommand('copy'); t.remove();
      }
      flash(note);
    } catch (e) { flash('Could not copy. Select the ID and copy it by hand.', true); }
  }
  function copySelectedIds() {
    const refs = members.filter(m => selected.indexOf(m.id) !== -1).map(m => m.ref).filter(Boolean);
    copyText(refs.join(', '), refs.length + (refs.length === 1 ? ' ID' : ' IDs') + ' copied. Paste them into any search.');
  }
  function toggle(id) { setSelected(sel => (sel.indexOf(id) === -1 ? sel.concat([id]) : sel.filter(x => x !== id))); }
  function openCandidate(m) { navigate('/benches/' + bench.ref + '/candidates/' + (m.ref || m.candidate_id)); }
  function sortHead(key, label) {
    const on = sort.key === key;
    return (
      <button type="button" className={'gtbn-sort' + (on ? ' gtbn-sort--on' : '')} onClick={() => sortBy(key)}
        aria-label={'Sort by ' + label + (on ? (sort.dir === 'asc' ? ', currently ascending' : ', currently descending') : '')}>
        <span>{label}</span><span className="gtbn-sort-ic" aria-hidden="true">{on ? (sort.dir === 'asc' ? '↑' : '↓') : '↕'}</span>
      </button>
    );
  }
  function sortBy(key) { setSort(s => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' })); }
  function scrollBar(dir) { const el = barRef.current; if (el) el.scrollBy({ left: dir * Math.max(240, el.clientWidth * 0.6), behavior: 'smooth' }); }

  function dropProps(target) {
    if (archived) return {};
    return {
      onDragOver: e => { if (!dragIds) return; e.preventDefault(); if (dropOn !== target) setDropOn(target); },
      onDragLeave: () => setDropOn(o => (o === target ? null : o)),
      onDrop: e => {
        e.preventDefault(); setDropOn(null);
        const ids = dragIds; setDragIds(null);
        if (ids && ids.length) moveTo(ids, target === NONE ? null : target);
      },
    };
  }

  function headerLabel(key) {
    if (key.indexOf('f:') === 0) return (fieldByKey[key.slice(2)] || {}).label;
    return STD_BY_KEY[key].label;
  }
  function widthOf(key) {
    if (key.indexOf('f:') === 0) { const f = fieldByKey[key.slice(2)]; return f && (f.type === 'text' || f.type === 'ai') ? 220 : f && f.type === 'yesno' ? 110 : 150; }
    return STD_BY_KEY[key].width;
  }

  function renderCell(key, m) {
    if (key === 'name') {
      return (
        <div className="gtbn-pinrow">
          <span className="gtbn-pin-check">
            <input type="checkbox" className="gtpb-check" checked={selected.indexOf(m.id) !== -1} onChange={() => toggle(m.id)} aria-label={'Select ' + m.full_name} />
          </span>
          <span className="gtbn-pin-id">
            <button type="button" className="gtbn-idcopy" onClick={() => copyText(m.ref, 'Copied ' + m.ref)} title="Copy this ID" aria-label={'Copy ID ' + m.ref}>
              <span className="gtbn-idtext">{m.ref}</span>
              <span className="gtbn-idicon" aria-hidden="true">⧉</span>
            </button>
          </span>
          <span className="gtbn-pin-name">
            {!archived && <span className="gtbn-grip" title="Drag onto a bucket" aria-hidden="true">⠿</span>}
            <span className="gtbn-av" style={{ background: avatarColour(m.full_name) }}>{initials(m.full_name)}</span>
            <button type="button" className="gtpl-name gtbn-clip" title={m.full_name} onClick={() => openCandidate(m)}>{m.full_name}</button>
            <TempDot row={m} />{/* C1-EMAIL */}
          </span>
        </div>
      );
    }
    if (key === 'bucket') {
      if (archived) {
        const b = bucketById[m.stage_id];
        return b ? <span className="gtbn-pill" style={{ background: bucketColour(b.tone).bg, color: bucketColour(b.tone).fg }}>{b.label}</span> : <span className="gtbn-muted">None</span>;
      }
      return <BucketPill current={bucketById[m.stage_id] || null} buckets={buckets} onPick={id => moveTo([m.id], id)} />;
    }
    if (key === 'roles') {
      return <RolesCell m={m} onOpenRole={ref => navigate('/roles/' + ref)} />;
    }
    if (key === 'last_touched_at') {
      return <span className={staleDays(m.last_touched_at) >= 30 ? 'gtbn-stale' : ''}>{niceDate(m.last_touched_at)}</span>;
    }
    if (key.indexOf('f:') === 0) return <CustomCell field={fieldByKey[key.slice(2)]} m={m} archived={archived}
      onAsk={() => setAiRun({ key: key.slice(2), memberIds: [m.id] })}
      editing={editCell && editCell.id === m.id && editCell.key === key.slice(2)}
      onEdit={() => setEditCell({ id: m.id, key: key.slice(2) })} onCancel={() => setEditCell(null)}
      onSave={v => saveCell(m, key.slice(2), v)} />;
    const c = STD_BY_KEY[key];
    const out = c.cell ? c.cell(m) : c.value(m);
    if (out == null || out === '') return <span className="gtbn-muted">—</span>;
    return typeof out === 'string' || typeof out === 'number' ? <span className="gtbn-clip" title={String(out)}>{out}</span> : out;
  }

  return (
    <div className="gtbn-page">
      <button className="gtr-back" onClick={() => navigate('/benches')}>← Benches</button>

      <div className="gtbn-head">
        <div className="gtbn-head-main">
          <div className="gtbn-head-title">
            <h1>{bench.name}</h1>
            <span className="gtr-refbadge">{bench.ref}</span>
            {archived && <span className="gtbn-archived">Archived</span>}
          </div>
          <div className="gtbn-head-meta">
            <span>{bench.client_name || 'No client · market map'}</span>
            {bench.location && <span>· {bench.location}</span>}
            {bench.expertise_domain && <span>· {words(bench.expertise_domain)}</span>}
            <span>· {bench.owner_name ? 'Owned by ' + bench.owner_name : <button type="button" className="gtbn-link" onClick={assignToMe}>Assign to me</button>}</span>
            <span className="gtbn-quiet">· Never contacts anyone</span>
          </div>
          {bench.brief && <p className="gtbn-brief">{bench.brief}</p>}
          {members.length > 0 && (() => { const tc = tempCounts(members); return ( // C1-EMAIL: how warm the bench is
            <div className="gttemp-bar" title="Hot: replied in 30 days. Warm: replied in 60 days, or contacted in 60 days.">
              <span className="gttemp-bar-lead"><b>{tc.hot}</b> of {members.length} hot <BenchTempTrend benchId={bench.id} authFetch={authFetch} hotNow={tc.hot} /></span>
              {[['hot', 'hot'], ['warm', 'warm'], ['cold', 'cold'], ['never', 'never contacted']].map(([k, l]) => { const on = temps.indexOf(k) !== -1; return ( // C1-BENCHTEMP
                <button type="button" key={k} aria-pressed={on} title={on ? 'Stop filtering by ' + l : 'Show only ' + l}
                  className={'gttemp gttemp--' + k + ' gttemp--label gttemp-pick' + (on ? ' gttemp-pick--on' : '')}
                  onClick={() => setTemps(t => (t.indexOf(k) === -1 ? t.concat([k]) : t.filter(x => x !== k)))}>
                  <i aria-hidden="true" /><span>{tc[k]} {l}</span>
                </button>
              ); })}
              {temps.length > 0 && <button type="button" className="gttemp-clear" onClick={() => setTemps([])}>Show everyone</button>}
              <div className="gttemp-bar-track" aria-hidden="true">{['hot', 'warm', 'cold', 'never'].map(k => tc[k] ? <i key={k} className={'gttemp-seg gttemp-seg--' + k + (temps.length && temps.indexOf(k) === -1 ? ' gttemp-seg--dim' : '')} style={{ flexGrow: tc[k] }} onClick={() => setTemps(t => (t.indexOf(k) === -1 ? t.concat([k]) : t.filter(x => x !== k)))} /> : null)}</div>
            </div>
          ); })()}
          {members.length > 0 && <BenchEmailStats benchId={bench.id} authFetch={authFetch} refreshKey={members} />}{/* C1-REPLYRATE */}
        </div>
        <div className="gtbn-head-acts">
          {dupGroups.length > 0 && !archived && (
            <button className="gtc-btn-ghost gtbx-dupbtn" onClick={() => setDupOpen(true)}>
              <span className="gtbx-dupdot" aria-hidden="true" />{dupGroups.length === 1 ? '1 possible duplicate' : dupGroups.length + ' possible duplicates'}
            </button>
          )}
          {members.some(m => ['warm', 'cold'].indexOf(tempOf(m).key) !== -1) && !archived && ( // C1-EMAIL: one-click warm-up
            <button className="gtc-btn-ghost" title="Email everyone on this bench who is warm or cold" onClick={() => setEmailFor(members.filter(m => ['warm', 'cold'].indexOf(tempOf(m).key) !== -1).map(m => m.candidate_id))}>
              Warm up {members.filter(m => ['warm', 'cold'].indexOf(tempOf(m).key) !== -1).length}
            </button>
          )}
          <button className="gtc-btn-ghost" onClick={() => setActivityTab('activity')}>Activity</button>
          <button className="gtc-btn-ghost" onClick={() => setEditing(true)}>Edit bench</button>
          {archived
            ? <button className="gtc-btn-ghost" onClick={() => setStatus('active')}>Restore</button>
            : <button className="gtc-btn-ghost" onClick={() => setStatus('archived')}>Archive</button>}
          {members.length === 0 && <button className="gtc-btn-ghost gtr-danger" onClick={deleteBench}>Delete</button>}
        </div>
      </div>

      {/* Buckets across the top. They scroll sideways when they run out of room. */}
      <div className="gtbn-bucketbar">
        <div className="gtbn-trackwrap">
        {overflow.left && <button type="button" className="gtbn-scroll gtbn-scroll--l" aria-label="Scroll buckets left" onClick={() => scrollBar(-1)}>‹</button>}
        <div className="gtbn-buckets-track" ref={barRef} onScroll={measure}>
          <button type="button" className={'gtbn-btab gtbn-btab--all' + (active === ALL ? ' gtbn-btab--on' : '')} onClick={() => setActive(ALL)}>
            <span className="gtbn-btab-label">All people</span><span className="gtbn-btab-n">{members.length}</span>
          </button>
          <button type="button" className={'gtbn-btab gtbn-btab--sugg' + (active === SUGGESTED ? ' gtbn-btab--on' : '')} onClick={() => setActive(SUGGESTED)}>
            <span className="gtbn-btab-label">Suggested</span><span className="gtbn-btab-n">{suggestedN}</span>
          </button>
          <button type="button" className={'gtbn-btab gtbn-btab--sugg' + (active === INTERVIEW ? ' gtbn-btab--on' : '')} onClick={() => setActive(INTERVIEW)}>
            <span className="gtbn-btab-label">Interview</span>
          </button>
          {buckets.map(b => (
            <button type="button" key={b.id}
              className={'gtbn-btab' + (active === b.id ? ' gtbn-btab--on' : '') + (dropOn === b.id ? ' gtbn-btab--drop' : '')}
              style={{ '--bg': bucketColour(b.tone).bg, '--fg': bucketColour(b.tone).fg, '--strong': bucketColour(b.tone).strong }}
              onClick={() => setActive(b.id)} {...dropProps(b.id)}>
              <span className="gtbn-btab-label">{b.label}</span>
              <span className="gtbn-btab-n">{countIn(b.id)}</span>
            </button>
          ))}
          {noBucket.length > 0 && (
            <button type="button"
              className={'gtbn-btab gtbn-btab--none' + (active === NONE ? ' gtbn-btab--on' : '') + (dropOn === NONE ? ' gtbn-btab--drop' : '')}
              onClick={() => setActive(NONE)} {...dropProps(NONE)}>
              <span className="gtbn-btab-label">Not in a bucket</span><span className="gtbn-btab-n">{noBucket.length}</span>
            </button>
          )}
          {!archived && (newBucket !== null ? (
            <div className="gtbn-btab gtbn-btab--new">
              <input autoFocus value={newBucket} maxLength={60} placeholder="Bucket name" aria-label="New bucket name"
                onChange={e => setNewBucket(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') createBucket(newBucket); if (e.key === 'Escape') setNewBucket(null); }}
                onBlur={() => createBucket(newBucket)} />
            </div>
          ) : (
            <button type="button" className="gtbn-btab gtbn-btab--add" onClick={() => setNewBucket('')}>+ New bucket</button>
          ))}
        </div>
        {overflow.right && <button type="button" className="gtbn-scroll gtbn-scroll--r" aria-label="Scroll buckets right" onClick={() => scrollBar(1)}>›</button>}
        </div>
        {!archived && buckets.length > 0 && <button type="button" className="gtc-btn-ghost gtbn-editbuckets" onClick={() => setManaging(true)}>Edit buckets</button>}
      </div>

      {active === SUGGESTED ? (
        <div className="gtbn-sugwrap">
          <GtSourcingPanel kind="bench" ownerId={bench.id} getToken={getToken} buckets={buckets} flash={flash}
            onOpenCandidate={s => navigate('/benches/' + bench.ref + '/candidates/' + (s.ref || s.candidate_id))}
            onChanged={onSourcingChanged} />
        </div>
      ) : active === INTERVIEW ? (
        <div className="gtbn-sugwrap">
          <GtInterviewPlan kind="bench" ownerId={bench.id} getToken={getToken} flash={flash} />
        </div>
      ) : (<>
      <div className="gtbn-toolbar">
        <div className="gtbn-viewing">
          <b>{activeLabel}</b>
          <span className="gtbn-count"><b>{sorted.length}</b> of {inBucket.length}</span>
        </div>
        <input className="gtsb-search gtbn-search" placeholder={'Search ' + (active === ALL ? 'this bench' : activeLabel) + ', or paste IDs…'} value={q} onChange={e => setQ(e.target.value)} />
        <div className="gtbn-spacer" />
        <FiltersButton filters={filters} setFilters={setFilters} members={inBucket} list={allFilters} />
        <SortButton sort={sort} setSort={setSort} options={[{ key: 'ref', label: 'ID' }].concat(columns.map(k => ({ key: k, label: headerLabel(k) })))} />
        <button className={'gtc-btn-ghost' + (columnsOpen ? ' gtbn-on' : '')} onClick={() => setColumnsOpen(true)}>Columns</button>
        <button className="gtc-btn-ghost" onClick={exportCsv} disabled={!sorted.length}
          title={selected.length ? 'Export the ' + selected.length + ' ticked' : 'Export everyone in this view'}>{selected.length ? 'Export ' + selected.length : 'Export'}</button>
        <button className="gtc-btn-ghost" onClick={() => startShare(shareList())} disabled={!sorted.length}
          title={selected.length ? 'Send the ' + selected.length + ' ticked to a client as a shortlist link' : 'Send everyone in this view to a client as a shortlist link'}>{selected.length ? 'Share ' + selected.length : 'Share'}</button>
        {!archived && <button className="gtc-primary" onClick={() => setAdding(true)}>Add people</button>}
      </div>

      {selected.length > 0 && (
        <div className="gtbn-bulk">
          <b>{selected.length} selected</b>
          {!archived && buckets.length > 0 && (
            <>
              <span className="gtbn-bulk-label">Move to bucket</span>
              <div className="gtbn-bulk-select"><GtSelect value={bulkBucket} options={bucketOpts} onChange={setBulkBucket} placeholder="Choose a bucket" /></div>
              <button className="gtc-btn-ghost" disabled={busy || bulkBucket === ''}
                onClick={async () => { await moveTo(selected, bulkBucket); setBulkBucket(''); setSelected([]); }}>Move</button>
            </>
          )}
          <button className="gtc-primary" onClick={() => setToRole(selected.slice())}>Move to role</button>
          <button className="gtc-btn-ghost" onClick={() => startShare(shareList())}>Share with client</button>
          <button className="gtc-btn-ghost" onClick={() => setEnrichFor(members.filter(m => selected.indexOf(m.id) !== -1).map(m => m.candidate_id))}>Enrich</button>
          {fields.some(f => f.type === 'ai') && <button className="gtc-btn-ghost" onClick={() => setAiRun({ key: null, memberIds: selected.slice() })}>Ask AI column</button>}
          <button className="gtc-btn-ghost" onClick={() => setEmailFor(members.filter(m => selected.indexOf(m.id) !== -1).map(m => m.candidate_id))}>Email</button>{/* C1-EMAIL */}
          <button className="gtc-btn-ghost" onClick={() => setSeqFor(members.filter(m => selected.indexOf(m.id) !== -1).map(m => m.candidate_id))}>Start sequence</button>{/* C1-SEQ2 */}
          <button className="gtc-btn-ghost" onClick={() => setFormFor(members.filter(m => selected.indexOf(m.id) !== -1).map(m => m.candidate_id))}>Send form</button>
          <button className="gtc-btn-ghost" onClick={copySelectedIds}>Copy IDs</button>
          {!archived && <button className="gtc-btn-ghost gtr-danger" onClick={() => removeMembers(selected)}>Take off bench</button>}
          <div className="gtbn-spacer" />
          <button className="gtc-btn-ghost" onClick={() => { setSelected([]); setBulkBucket(''); }}>Clear</button>
        </div>
      )}

      {formFor && (
        <GtSendFormModal candidateIds={formFor} source={{ kind: 'bench', id: bench.id, label: bench.name }} authFetch={authFetch} onClose={() => setFormFor(null)} />
      )}
      {emailFor && ( // C1-EMAIL
        <GtEmailModal candidateIds={emailFor} source={{ kind: 'bench', id: bench.id, label: bench.name }} authFetch={authFetch} onClose={() => { setEmailFor(null); load(); }} />
      )}
      {seqFor && ( // C1-SEQ2
        <GtSequenceStart candidateIds={seqFor} source={{ kind: 'bench', id: bench.id, label: bench.name }} authFetch={authFetch} navigate={navigate} onClose={() => setSeqFor(null)} />
      )}

      {enrichRuns.length > 0 && (
        <GtEnrichProgress runIds={enrichRuns} authFetch={authFetch} onFinished={onEnrichFinished} onDismiss={() => setEnrichRuns([])} />
      )}
      {aiRun && (
        <GtAiColumnRun bench={bench} fields={fields} initialKey={aiRun.key} memberIds={aiRun.memberIds} members={members}
          authFetch={authFetch} flash={flash} onClose={() => setAiRun(null)} onDone={load} />
      )}
      {dossierFor && <GtDossierView candidateId={dossierFor} getToken={getToken} onClose={() => setDossierFor(null)} />}
      {enrichFor && (
        <GtEnrichModal candidateIds={enrichFor} authFetch={authFetch} flash={flash} onClose={() => setEnrichFor(null)}
          onStarted={r => { setSelected([]); if (r.runs && r.runs.length) setEnrichRuns(r.runs.map(x => x.id)); }} />
      )}

      {members.length === 0 ? (
        <div className="gtsb-empty">
          <div className="gtsb-empty-mark">◆</div>
          <h3>{buckets.length === 0 ? 'Start with a bucket' : 'Nobody on this bench yet'}</h3>
          <p>{buckets.length === 0
            ? 'Create as many buckets as you like across the top, then add people into whichever bucket you choose.'
            : 'Add people from the pool into any bucket. You can move them between buckets at any time.'}</p>
          {!archived && (
            <div className="gtbn-row gtbn-center">
              {buckets.length === 0 && <button className="gtc-primary" onClick={() => setNewBucket('')}>+ New bucket</button>}
              <button className={buckets.length === 0 ? 'gtc-btn-ghost' : 'gtc-primary'} onClick={() => setAdding(true)}>Add people</button>
            </div>
          )}
        </div>
      ) : sorted.length === 0 ? (
        <div className="gtr-emptytab">{pastedRefs.length ? 'None of those ' + pastedRefs.length + ' IDs are in ' + activeLabel + '.' : ql || activeFilters.length || temps.length ? 'Nobody matches that search or those filters.' : 'Nobody in ' + activeLabel + ' yet. Add people, or drag rows here from another bucket.'}</div>
      ) : (
        <>
        <div className={'gtbn-tableframe' + (edges.left ? ' gtbn-edge-l' : '') + (edges.right ? ' gtbn-edge-r' : '')}>
        <div className="gtbn-tablewrap" ref={tableRef} onScroll={measureTable}>
          <table className="gtbn-table" style={{ minWidth: columns.reduce((t, k) => t + widthOf(k), 0) + 270 }}>
            <colgroup>
              {columns.map(key => <col key={key} style={{ width: widthOf(key) }} />)}
              <col style={{ width: 270 }} />
            </colgroup>
            <thead>
              <tr>
                {columns.map(key => (
                  <th key={key} className={key === 'name' ? 'gtbn-pin' : ''}>
                    {key === 'name' ? (
                      <div className="gtbn-pinrow">
                        <span className="gtbn-pin-check">
                          <input type="checkbox" className="gtpb-check" aria-label="Select everyone on this page"
                            checked={shown.length > 0 && shown.every(m => selected.indexOf(m.id) !== -1)}
                            onChange={e => {
                              const ids = shown.map(m => m.id);
                              setSelected(sel => (e.target.checked ? Array.from(new Set(sel.concat(ids))) : sel.filter(id => ids.indexOf(id) === -1)));
                            }} />
                        </span>
                        <span className="gtbn-pin-id">{sortHead('ref', 'ID')}</span>
                        <span className="gtbn-pin-name">{sortHead('name', 'Name')}</span>
                      </div>
                    ) : sortHead(key, headerLabel(key))}
                  </th>
                ))}
                <th className="gtbn-th-acts" aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {shown.map(m => {
                const isSel = selected.indexOf(m.id) !== -1;
                return (
                  <tr key={m.id} className={(isSel ? 'gtbn-tr--sel' : '') + (dragIds && dragIds.indexOf(m.id) !== -1 ? ' gtbn-tr--drag' : '')}
                    draggable={!archived}
                    onDragStart={e => {
                      const ids = isSel ? selected.slice() : [m.id];
                      setDragIds(ids);
                      e.dataTransfer.effectAllowed = 'move';
                      e.dataTransfer.setData('text/plain', ids.length + ' people');
                    }}
                    onDragEnd={() => { setDragIds(null); setDropOn(null); }}>
                    {columns.map(key => <td key={key} className={key === 'name' ? 'gtbn-pin' : ''}>{renderCell(key, m)}</td>)}
                    <td className="gtbn-td-acts">
                      <button className="gtr-editbtn gtr-editbtn-ghost" onClick={() => setDossierFor(m.candidate_id)} title="Open their dossier">Dossier</button>
                      <button className="gtr-editbtn gtr-editbtn-ghost" onClick={() => pdfFor(m)} title="Download their dossier as a PDF">PDF</button>
                      <button className="gtr-editbtn gtr-editbtn-ghost" onClick={() => setToRole([m.id])}>Move to role</button>
                      {!archived && <button className="gtr-editbtn gtr-editbtn-ghost gtr-danger" onClick={() => removeMembers([m.id])} aria-label={'Take ' + m.full_name + ' off the bench'}>×</button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </div>
        <div className="gtbn-pager">
          <div className="gtbn-pager-size">
            <span>Rows per page</span>
            <div className="gtbn-pager-select"><GtSelect value={pageSize} onChange={v => setPageSize(Number(v))} options={PAGE_SIZES.map(n => ({ value: n, label: String(n) }))} /></div>
          </div>
          <div className="gtbn-pager-nav">
            <button type="button" className="gtc-btn-ghost" disabled={safePage === 0} onClick={() => setPage(safePage - 1)}>Prev</button>
            <b>{safePage + 1} of {pages}</b>
            <button type="button" className="gtc-btn-ghost" disabled={safePage >= pages - 1} onClick={() => setPage(safePage + 1)}>Next</button>
          </div>
          <div className="gtbn-pager-count">{sorted.length ? ((safePage * pageSize) + 1) + '–' + ((safePage * pageSize) + shown.length) + ' of ' + sorted.length : '0 of 0'}</div>
        </div>
        </>
      )}

      </>)}

      {editing && <BenchForm bench={bench} authFetch={authFetch} flash={flash} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); load(); }} />}
      {managing && <BucketsModal bench={bench} buckets={buckets} authFetch={authFetch} flash={flash} onClose={() => setManaging(false)} onSaved={() => { setManaging(false); load(); }} />}
      {columnsOpen && (
        <ColumnsPanel bench={bench} columns={columns} fields={fields} authFetch={authFetch} flash={flash}
          onClose={() => setColumnsOpen(false)}
          onSaved={aiKeys => { setColumnsOpen(false); load(); if (aiKeys && aiKeys.length) setAiRun({ key: aiKeys[0], memberIds: null }); }} />
      )}
      {adding && (
        <AddPeopleModal bench={bench} buckets={buckets} members={members} defaultBucket={typeof active === 'number' ? active : (buckets[0] ? buckets[0].id : '')}
          authFetch={authFetch} flash={flash} onClose={() => setAdding(false)} onAdded={load} />
      )}
      {toRole && (
        <ToRoleModal bench={bench} memberIds={toRole} authFetch={authFetch} flash={flash}
          onClose={() => setToRole(null)} onDone={() => { setSelected([]); load(); }} />
      )}
      {activityTab && (
        <GtBenchActivity bench={bench} buckets={buckets} authFetch={authFetch} flash={flash} initialTab={activityTab}
          onClose={() => setActivityTab(null)} onRestored={load}
          onSelect={(ids, imp) => {
            setActivityTab(null); setQ(''); setFilters([]); setTemps([]);
            if (active === ALL) setSelected(ids); else { pendingSelect.current = ids; setActive(ALL); }
            flash(ids.length + (ids.length === 1 ? ' person' : ' people') + ' from ' + (imp.filename || 'that file') + ' selected.');
          }} />
      )}
      {dupOpen && (
        <GtBenchDuplicates groups={dupGroups} bucketById={bucketById} bucketColour={bucketColour}
          onClose={() => setDupOpen(false)} onMerge={ids => setMergeIds(ids)} />
      )}
      {mergeIds && (
        <MergeModal ids={mergeIds} authFetch={authFetch} flash={msg => flash(msg, true)}
          onClose={() => setMergeIds(null)} onDone={() => load()} />
      )}
      {share && !share.clientId && (
        <GtBenchClientPick count={share.people.length} authFetch={authFetch} flash={flash}
          onClose={() => setShare(null)} onPick={cid => setShare(s => ({ ...s, clientId: cid }))} />
      )}
      {share && share.clientId && (
        <GtShareCreate clientId={share.clientId} getToken={getToken} fromLabel="this bench"
          preselected={share.people.map(m => ({
            id: m.candidate_id, ref: m.ref, full_name: m.full_name, current_title: m.current_title,
            current_company: m.current_company, location: m.location, headline: m.headline, consent_status: m.consent_status,
          }))}
          onClose={() => setShare(null)} onCreated={() => { setSelected([]); load(); }} />
      )}
    </div>
  );
}

// A small popover anchored to a button, closed by Escape, scrolling or a click
// outside. Used by the bucket pill and the roles panel.
// C1-REPLYRATE: emails sent from this bench and how many people replied.
// Hidden until the bench has sent something.
function BenchEmailStats({ benchId, authFetch, refreshKey }) {
  const [st, setSt] = useState(null);
  const af = useRef(authFetch);
  af.current = authFetch;
  useEffect(() => {
    let live = true;
    af.current('/api/benches/' + benchId + '/email-stats').then((d) => { if (live) setSt(d); }).catch(() => null);
    return () => { live = false; };
  }, [benchId, refreshKey]);
  if (!st || !st.sent) return null;
  const pct = Math.round((st.replied / st.sent) * 100);
  return (
    <div className="gtbn-emailstats">
      Emails from this bench: <b>{st.sent}</b> {st.sent === 1 ? 'person' : 'people'} reached · <b>{st.replied}</b> replied ({pct}%)
    </div>
  );
}

function usePopover(panelClass) {
  const [pos, setPos] = useState(null);
  useEffect(() => {
    if (!pos) return undefined;
    function close(e) {
      if (e.type === 'keydown' && e.key !== 'Escape') return;
      if (e.type === 'scroll' && e.target && e.target.closest && e.target.closest('.' + panelClass)) return;
      setPos(null);
    }
    function outside(e) { if (!e.target.closest || !e.target.closest('.' + panelClass)) setPos(null); }
    window.addEventListener('keydown', close);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    document.addEventListener('mousedown', outside);
    return () => {
      window.removeEventListener('keydown', close);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      document.removeEventListener('mousedown', outside);
    };
  }, [pos, panelClass]);
  function openAt(el, width, height) {
    const rect = el.getBoundingClientRect();
    const left = Math.max(12, Math.min(rect.left, window.innerWidth - width - 12));
    const below = window.innerHeight - rect.bottom;
    setPos(below > height ? { left, top: rect.bottom + 6, width } : { left, bottom: window.innerHeight - rect.top + 6, width });
  }
  return [pos, openAt, () => setPos(null)];
}

function FiltersButton({ filters, setFilters, members, list = FILTERS }) {
  const btnRef = useRef(null);
  const [pos, openAt, close] = usePopover('gtbn-menu');
  const n = filters.length;
  return (
    <>
      <button type="button" ref={btnRef} className={'gtc-btn-ghost' + (n ? ' gtbn-on' : '')}
        onClick={() => (pos ? close() : openAt(btnRef.current, 260, 300))} aria-expanded={!!pos}>
        Filters{n ? ' · ' + n : ''}
      </button>
      {pos && (
        <div className="gtbn-menu" style={{ left: pos.left, top: pos.top, bottom: pos.bottom, width: pos.width }}>
          <div className="gtbn-bucketmenu-head">Show only people who are…</div>
          {list.map(f => {
            const on = filters.indexOf(f.key) !== -1;
            return (
              <label key={f.key} className="gtbn-menu-check">
                <input type="checkbox" className="gtpb-check" checked={on}
                  onChange={() => setFilters(x => (on ? x.filter(k => k !== f.key) : x.concat([f.key])))} />
                <span>{f.label}</span>
                <span className="gtbn-menu-n">{members.filter(f.test).length}</span>
              </label>
            );
          })}
          {n > 0 && <button type="button" className="gtbn-menu-clear" onClick={() => setFilters([])}>Clear filters</button>}
        </div>
      )}
    </>
  );
}

function SortButton({ sort, setSort, options }) {
  const btnRef = useRef(null);
  const [pos, openAt, close] = usePopover('gtbn-menu');
  const cur = options.find(o => o.key === sort.key);
  return (
    <>
      <button type="button" ref={btnRef} className="gtc-btn-ghost" onClick={() => (pos ? close() : openAt(btnRef.current, 240, 340))} aria-expanded={!!pos}>
        Sort {sort.dir === 'asc' ? '↑' : '↓'}
      </button>
      {pos && (
        <div className="gtbn-menu" style={{ left: pos.left, top: pos.top, bottom: pos.bottom, width: pos.width }}>
          <div className="gtbn-bucketmenu-head">Sort by{cur ? ': ' + cur.label : ''}</div>
          <div className="gtbn-menu-dir">
            <button type="button" className={sort.dir === 'asc' ? 'gtbn-on' : ''} onClick={() => setSort(s => ({ ...s, dir: 'asc' }))}>↑ Ascending</button>
            <button type="button" className={sort.dir === 'desc' ? 'gtbn-on' : ''} onClick={() => setSort(s => ({ ...s, dir: 'desc' }))}>↓ Descending</button>
          </div>
          {options.map(o => (
            <button type="button" key={o.key} className={'gtbn-bucketmenu-item' + (o.key === sort.key ? ' gtbn-bucketmenu-item--on' : '')}
              onClick={() => { setSort(s => ({ key: o.key, dir: s.dir })); close(); }}>
              <span className="gtbn-clip">{o.label}</span>
              {o.key === sort.key && <span className="gtbn-bucketmenu-tick" aria-hidden="true">✓</span>}
            </button>
          ))}
        </div>
      )}
    </>
  );
}

function BucketPill({ current, buckets, onPick }) {
  const btnRef = useRef(null);
  const [pos, openAt, close] = usePopover('gtbn-bucketmenu');
  const c = current ? bucketColour(current.tone) : null;
  return (
    <>
      <button type="button" ref={btnRef} className={'gtbn-bpill' + (current ? '' : ' gtbn-bpill--none')}
        style={c ? { background: c.bg, color: c.fg } : undefined}
        onClick={() => (pos ? close() : openAt(btnRef.current, 220, 280))} aria-haspopup="menu" aria-expanded={!!pos}>
        <span className="gtbn-clip">{current ? current.label : 'No bucket'}</span><span className="gtbn-bpill-caret" aria-hidden="true">▾</span>
      </button>
      {pos && (
        <div className="gtbn-bucketmenu" role="menu" style={{ left: pos.left, top: pos.top, bottom: pos.bottom, width: pos.width }}>
          <div className="gtbn-bucketmenu-head">Move to bucket</div>
          {buckets.map(b => {
            const bc = bucketColour(b.tone);
            const on = current && current.id === b.id;
            return (
              <button type="button" role="menuitem" key={b.id} className={'gtbn-bucketmenu-item' + (on ? ' gtbn-bucketmenu-item--on' : '')}
                onClick={() => { close(); if (!on) onPick(b.id); }}>
                <span className="gtbn-bucketmenu-sw" style={{ background: bc.bg, boxShadow: 'inset 0 0 0 2px ' + bc.strong }} />
                <span className="gtbn-clip">{b.label}</span>
                {on && <span className="gtbn-bucketmenu-tick" aria-hidden="true">✓</span>}
              </button>
            );
          })}
          {buckets.length === 0 && <div className="gtr-empty-sm">No buckets yet.</div>}
        </div>
      )}
    </>
  );
}

// Roles: one line with the role they are furthest along in, and a panel
// listing every role when there are more. The row never grows.
function RolesCell({ m, onOpenRole }) {
  const [open, setOpen] = useState(null);
  const btnRef = useRef(null);
  const roles = m.roles || [];

  useEffect(() => {
    if (!open) return undefined;
    function close(e) {
      if (e.type === 'keydown' && e.key !== 'Escape') return;
      // Scrolling the panel's own list must not close it.
      if (e.type === 'scroll' && e.target && e.target.closest && e.target.closest('.gtbn-rolepanel')) return;
      setOpen(null);
    }
    function outside(e) { if (!e.target.closest || !e.target.closest('.gtbn-rolepanel')) setOpen(null); }
    window.addEventListener('keydown', close);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    document.addEventListener('mousedown', outside);
    return () => {
      window.removeEventListener('keydown', close);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      document.removeEventListener('mousedown', outside);
    };
  }, [open]);

  if (!roles.length) return <span className="gtbn-muted">None</span>;
  const live = r => r.outcome !== 'lost' && r.outcome !== 'won' && ['filled', 'closed'].indexOf(r.status) === -1;
  const ordered = roles.slice().sort((a, b) => (Number(live(b)) - Number(live(a))) || ((b.position || 0) - (a.position || 0)));
  const top = ordered[0];
  const chip = r => (
    <span className={'gtbn-rolechip' + (r.outcome === 'lost' ? ' gtbn-rolechip--out' : r.outcome === 'won' ? ' gtbn-rolechip--won' : '')}>
      <span className="gtbn-rolechip-t">{r.title}</span>{r.stage && <span className="gtbn-rolechip-s">{r.stage}</span>}
    </span>
  );

  function toggle() {
    if (open) { setOpen(null); return; }
    const rect = btnRef.current.getBoundingClientRect();
    const width = 340;
    const left = Math.max(12, Math.min(rect.left, window.innerWidth - width - 12));
    const below = window.innerHeight - rect.bottom;
    setOpen(below > 260 ? { left, top: rect.bottom + 6, width } : { left, bottom: window.innerHeight - rect.top + 6, width });
  }

  const liveCount = roles.filter(live).length;
  return (
    <div className="gtbn-roles">
      <button type="button" className="gtbn-rolebtn" onClick={() => onOpenRole(top.ref)} title={'Open ' + top.title}>{chip(top)}</button>
      {roles.length > 1 && (
        <button type="button" ref={btnRef} className="gtbn-rolemore" onClick={toggle} aria-expanded={!!open}>+{roles.length - 1} more</button>
      )}
      {open && (
        <div className="gtbn-rolepanel" role="dialog" aria-label={'Roles for ' + m.full_name}
          style={{ left: open.left, top: open.top, bottom: open.bottom, width: open.width }}>
          <div className="gtbn-rolepanel-head">
            <b>{m.full_name}</b>
            <span>{roles.length} roles · {liveCount} live</span>
          </div>
          <div className="gtbn-rolepanel-list">
            {ordered.map(r => (
              <button type="button" key={r.id} className={'gtbn-rolerow' + (live(r) ? '' : ' gtbn-rolerow--done')} onClick={() => { setOpen(null); onOpenRole(r.ref); }}>
                <span className="gtbn-rolerow-main">
                  <span className="gtbn-rolerow-title">{r.title}</span>
                  {r.client && <span className="gtbn-rolerow-client">{r.client}</span>}
                </span>
                <span className={'gtbn-rolerow-stage' + (r.outcome === 'won' ? ' gtbn-rolerow-stage--won' : r.outcome === 'lost' ? ' gtbn-rolerow-stage--out' : '')}>{r.stage || 'No stage'}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// A bench's own column, edited in the cell.
function CustomCell({ field, m, archived, editing, onEdit, onCancel, onSave, onAsk }) {
  const raw = (m.custom_values || {})[field.key];
  const [draft, setDraft] = useState(raw == null ? '' : String(raw));
  useEffect(() => { if (editing) setDraft(raw == null ? '' : String(raw)); }, [editing, raw]);

  if (field.type === 'ai') {
    // Answered by the column's question. Click to see the evidence or ask again.
    const v = raw && typeof raw === 'object' ? raw : null;
    const tip = v ? [v.e, v.c != null ? Math.round(v.c * 100) + '% sure' : null].filter(Boolean).join(' · ') : 'Not asked yet';
    const tone = !v ? 'none' : ['yes', 'no', 'unknown'].indexOf(v.a) !== -1 ? v.a : 'text';
    const label = !v ? 'Ask' : v.a === 'yes' ? 'Yes' : v.a === 'no' ? 'No' : v.a === 'unknown' ? 'Unknown' : v.a;
    return (
      <button type="button" className={'gtbn-ai gtbn-ai--' + tone} title={tip} disabled={archived && !v} onClick={onAsk}>
        <span className="gtbn-ai-a">{label}</span>{v && v.e && <span className="gtbn-ai-e">{v.e}</span>}
      </button>
    );
  }
  if (field.type === 'yesno') {
    const next = raw === true ? false : raw === false ? null : true;
    return (
      <button type="button" disabled={archived} className={'gtbn-yesno' + (raw === true ? ' gtbn-yesno--y' : raw === false ? ' gtbn-yesno--n' : '')}
        onClick={() => onSave(next)} title="Click to change">
        {raw === true ? 'Yes' : raw === false ? 'No' : '—'}
      </button>
    );
  }
  if (field.type === 'select') {
    if (archived) return raw ? <span>{raw}</span> : <span className="gtbn-muted">—</span>;
    return (
      <div className="gtbn-bucketcell">
        <GtSelect value={raw == null ? '' : raw} placeholder="—"
          options={[{ value: '', label: 'Clear' }].concat((field.options || []).map(o => ({ value: o, label: o })))}
          onChange={v => onSave(v === '' ? null : v)} />
      </div>
    );
  }
  if (field.type === 'date') {
    if (archived) return raw ? niceDate(raw) : <span className="gtbn-muted">—</span>;
    return (
      <div className="gtbn-bucketcell">
        <GtDatePicker value={raw || ''} onChange={v => onSave(v ? String(v).slice(0, 10) : null)} />
      </div>
    );
  }
  if (editing) {
    return (
      <input className="gtbn-cellinput" autoFocus value={draft}
        inputMode={field.type === 'number' ? 'decimal' : undefined}
        placeholder={field.type === 'url' ? 'example.com' : ''}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') onSave(draft.trim() === '' ? null : draft); if (e.key === 'Escape') onCancel(); }}
        onBlur={() => ((draft === (raw == null ? '' : String(raw))) ? onCancel() : onSave(draft.trim() === '' ? null : draft))} />
    );
  }
  let shown;
  if (raw == null || raw === '') shown = <span className="gtbn-muted">{archived ? '—' : 'Add'}</span>;
  else if (field.type === 'url') shown = <a href={raw} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}>{String(raw).replace(/^https?:\/\//, '').replace(/\/$/, '')}</a>;
  else if (field.type === 'number') shown = Number(raw).toLocaleString('en-GB');
  else shown = String(raw);
  return (
    <div className={'gtbn-editable' + (archived ? '' : ' gtbn-editable--on')} onClick={archived ? undefined : onEdit}
      role={archived ? undefined : 'button'} tabIndex={archived ? undefined : 0}
      onKeyDown={archived ? undefined : (e => { if (e.key === 'Enter') onEdit(); })}>{shown}</div>
  );
}

// ── Columns: show, hide, order, and the bench's own columns ─────────────
function ColumnsPanel({ bench, columns, fields, authFetch, flash, onClose, onSaved }) {
  const [order, setOrder] = useState(columns.slice());
  const [own, setOwn] = useState(fields.map(f => {
    const o = f.options && !Array.isArray(f.options) ? f.options : null;
    return { id: f.id, key: f.key, label: f.label, type: f.type, options: Array.isArray(f.options) ? f.options.join(', ') : '',
      question: (o && o.question) || '', answer: (o && o.answer) || 'yesno' };
  }));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const ownLabel = {};
  fields.forEach(f => { ownLabel['f:' + f.key] = f.label; });
  const labelOf = k => (STD_BY_KEY[k] ? STD_BY_KEY[k].label : ownLabel[k] || 'New column');
  const hiddenStd = STD.filter(c => order.indexOf(c.key) === -1);
  const hiddenOwn = fields.filter(f => order.indexOf('f:' + f.key) === -1);

  function move(i, d) {
    setOrder(o => { const j = i + d; if (j < 1 || j >= o.length || i < 1) return o; const c = o.slice(); const t = c[i]; c[i] = c[j]; c[j] = t; return c; });
  }
  function updateOwn(i, k, v) { setOwn(list => list.map((x, j) => (j === i ? { ...x, [k]: v } : x))); }

  async function save() {
    setErr(null);
    const cleanOwn = own.filter(f => f.id != null || f.label.trim());
    if (cleanOwn.some(f => !f.label.trim())) { setErr('Every column needs a name.'); return; }
    if (cleanOwn.some(f => f.type === 'ai' && !f.question.trim())) { setErr('Every AI column needs a question.'); return; }
    const lost = fields.filter(f => !cleanOwn.some(x => x.id === f.id));
    const asked = (f, x) => f.type === 'ai' && x.type === 'ai'
      && (((f.options || {}).question || '') !== x.question.trim() || ((f.options || {}).answer || 'yesno') !== x.answer);
    const retyped = fields.filter(f => cleanOwn.some(x => x.id === f.id && (x.type !== f.type || asked(f, x))));
    if (lost.length || retyped.length) {
      const names = lost.concat(retyped).map(f => '"' + f.label + '"').join(', ');
      const ok = await gtConfirm({
        title: 'Clear what is filled in?',
        message: 'Removing a column, changing what kind of column it is, or changing an AI question clears what has been filled in for ' + names + ' on this bench.',
        confirmLabel: 'Go ahead', tone: 'danger',
      });
      if (!ok) return;
    }
    setBusy(true);
    try {
      const r = await authFetch('/api/benches/' + bench.id + '/fields', {
        method: 'PUT',
        body: JSON.stringify({ fields: cleanOwn.map(f => ({ id: f.id, label: f.label, type: f.type,
          options: f.type === 'ai' ? { question: f.question.trim(), answer: f.answer } : f.options.split(',').map(s => s.trim()).filter(Boolean) })) }),
      });
      // New columns are shown straight away, at the end.
      const before = new Set(fields.map(f => f.key));
      const added = (r.fields || []).filter(f => !before.has(f.key)).map(f => 'f:' + f.key);
      const live = new Set((r.fields || []).map(f => 'f:' + f.key));
      const nextOrder = order.filter(k => k.indexOf('f:') !== 0 || live.has(k)).concat(added);
      await authFetch('/api/benches/' + bench.id + '/columns', { method: 'PUT', body: JSON.stringify({ columns: nextOrder }) });
      // New AI columns, and ones with a new question, are offered a first run.
      const changedAi = new Set(retyped.filter(f => f.type === 'ai').map(f => f.key));
      const aiKeys = (r.fields || []).filter(f => f.type === 'ai' && (!before.has(f.key) || changedAi.has(f.key))).map(f => f.key);
      flash('Columns saved'); onSaved(aiKeys);
    } catch (e) { setErr(e.message); }
    setBusy(false);
  }

  return (
    <div className="gtc-modal-backdrop" onClick={() => !busy && onClose()}>
      <div className="gtc-modal gtbn-colmodal" onClick={e => e.stopPropagation()}>
        <div className="gtc-modal-head">Columns on this bench</div>
        <div className="gtc-modal-body gtbn-colbody">
          <div className="gtbn-colpane">
            <div className="gtbn-colhead">Showing, in this order</div>
            <div className="gtbn-collist">
              {order.map((k, i) => (
                <div key={k} className="gtbn-colrow">
                  <div className="gtbn-bucket-order">
                    <button type="button" aria-label="Move up" disabled={i <= 1} onClick={() => move(i, -1)}>▲</button>
                    <button type="button" aria-label="Move down" disabled={i === 0 || i === order.length - 1} onClick={() => move(i, 1)}>▼</button>
                  </div>
                  <span className="gtbn-colname">{labelOf(k)}{k.indexOf('f:') === 0 && <em className="gtbn-own">this bench</em>}</span>
                  {k === 'name'
                    ? <span className="gtbn-muted">always shown</span>
                    : <button type="button" className="gtbn-colhide" onClick={() => setOrder(o => o.filter(x => x !== k))}>Hide</button>}
                </div>
              ))}
            </div>
            {(hiddenStd.length > 0 || hiddenOwn.length > 0) && (
              <>
                <div className="gtbn-colhead">Not showing</div>
                <div className="gtbn-coladds">
                  {hiddenOwn.map(f => <button type="button" key={f.key} className="gtbn-coladd gtbn-coladd--own" onClick={() => setOrder(o => o.concat(['f:' + f.key]))}>+ {f.label}</button>)}
                  {hiddenStd.map(c => <button type="button" key={c.key} className="gtbn-coladd" onClick={() => setOrder(o => o.concat([c.key]))}>+ {c.label}</button>)}
                </div>
              </>
            )}
          </div>
          <div className="gtbn-colpane">
            <div className="gtbn-colhead">This bench's own columns</div>
            <p className="gtbn-help">Add anything you want to track for the people on this bench. You fill these in right in the table. An AI question is answered for everyone from what we hold about them.</p>
            {own.length === 0 && <div className="gtr-empty-sm">None yet.</div>}
            <div className="gtbn-ownlist">
              {own.map((f, i) => (
                <div key={f.id || 'new' + i} className="gtbn-ownrow">
                  <input value={f.label} maxLength={60} placeholder="Column name" aria-label="Column name"
                    autoFocus={f.id == null && i === own.length - 1} onChange={e => updateOwn(i, 'label', e.target.value)} />
                  <div className="gtbn-owntype"><GtSelect value={f.type} options={FIELD_TYPES} onChange={v => updateOwn(i, 'type', v)} /></div>
                  <button type="button" className="gtbn-bucket-x" aria-label={'Remove ' + (f.label || 'column')} onClick={() => setOwn(l => l.filter((_, j) => j !== i))}>×</button>
                  {f.type === 'select' && (
                    <input className="gtbn-ownopts" value={f.options} placeholder="Choices, separated by commas" aria-label="Choices"
                      onChange={e => updateOwn(i, 'options', e.target.value)} />
                  )}
                  {f.type === 'ai' && (
                    <div className="gtbn-aiq">
                      <textarea value={f.question} maxLength={300} rows={2} aria-label="Question"
                        placeholder="The question, e.g. Has shipped LLM evaluations in production?"
                        onChange={e => updateOwn(i, 'question', e.target.value)} />
                      <div className="gtbn-owntype"><GtSelect value={f.answer} options={AI_ANSWER} onChange={v => updateOwn(i, 'answer', v)} /></div>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <button type="button" className="gtc-btn-ghost" onClick={() => setOwn(l => l.concat([{ id: null, key: null, label: '', type: 'text', options: '', question: '', answer: 'yesno' }]))}>+ Add a column</button>
          </div>
        </div>
        {err && <div className="gtc-error gtbn-err gtbn-pad">{err}</div>}
        <div className="gtc-modal-foot">
          <button className="gtc-btn-ghost" disabled={busy} onClick={onClose}>Cancel</button>
          <button className="gtc-primary" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save columns'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Rename, recolour, reorder and remove buckets ────────────────────────
function BucketsModal({ bench, buckets, authFetch, flash, onClose, onSaved }) {
  const [rows, setRows] = useState(() => buckets.map(b => ({ id: b.id, label: b.label, tone: b.tone || 'slate', n: b.candidate_count })));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  function update(i, k, v) { setRows(r => r.map((x, j) => (j === i ? { ...x, [k]: v } : x))); }
  function moveRow(i, d) {
    setRows(r => { const j = i + d; if (j < 0 || j >= r.length) return r; const c = r.slice(); const t = c[i]; c[i] = c[j]; c[j] = t; return c; });
  }

  async function save() {
    setErr(null);
    const clean = rows.filter(r => r.id != null || r.label.trim());
    if (clean.some(r => !r.label.trim())) { setErr('Every bucket needs a name.'); return; }
    setBusy(true);
    try {
      await authFetch('/api/benches/' + bench.id + '/buckets', {
        method: 'PUT', body: JSON.stringify({ buckets: clean.map(r => ({ id: r.id, label: r.label, tone: r.tone })) }),
      });
      flash('Buckets saved'); onSaved();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  }

  return (
    <div className="gtc-modal-backdrop" onClick={() => !busy && onClose()}>
      <div className="gtc-modal" onClick={e => e.stopPropagation()}>
        <div className="gtc-modal-head">Edit buckets</div>
        <div className="gtc-modal-body">
          <p className="gtbn-help">Rename, recolour and reorder. A bucket with people in it cannot be removed until they are moved.</p>
          <div className="gtbn-bucketedit">
            {rows.map((r, i) => (
              <div key={r.id || 'new' + i} className="gtbn-bucket">
                <div className="gtbn-bucket-order">
                  <button type="button" aria-label="Move up" disabled={i === 0} onClick={() => moveRow(i, -1)}>▲</button>
                  <button type="button" aria-label="Move down" disabled={i === rows.length - 1} onClick={() => moveRow(i, 1)}>▼</button>
                </div>
                <input value={r.label} maxLength={60} placeholder="Bucket name" aria-label="Bucket name" onChange={e => update(i, 'label', e.target.value)} />
                <div className="gtbn-swatches" role="radiogroup" aria-label="Colour">
                  {BUCKET_TONES.map(t => (
                    <button key={t} type="button" role="radio" aria-checked={r.tone === t} aria-label={BUCKET[t].name} title={BUCKET[t].name}
                      className={'gtbn-swatch' + (r.tone === t ? ' gtbn-swatch--on' : '')}
                      style={{ background: BUCKET[t].strong }} onClick={() => update(i, 'tone', t)} />
                  ))}
                </div>
                <span className="gtbn-bucket-n">{r.n ? r.n + (r.n === 1 ? ' person' : ' people') : ''}</span>
                <button type="button" className="gtbn-bucket-x" aria-label={'Remove ' + (r.label || 'bucket')}
                  disabled={r.n > 0} title={r.n > 0 ? 'Move the people out first' : 'Remove'}
                  onClick={() => setRows(x => x.filter((_, j) => j !== i))}>×</button>
              </div>
            ))}
          </div>
          <button type="button" className="gtc-btn-ghost" onClick={() => setRows(r => r.concat([{ id: null, label: '', tone: BUCKET_TONES[r.length % BUCKET_TONES.length], n: 0 }]))}>+ Add a bucket</button>
          {err && <div className="gtc-error gtbn-err">{err}</div>}
        </div>
        <div className="gtc-modal-foot">
          <button className="gtc-btn-ghost" disabled={busy} onClick={onClose}>Cancel</button>
          <button className="gtc-primary" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save buckets'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Add people from the pool, into the bucket of your choice ─────────────
function AddPeopleModal({ bench, buckets, members, defaultBucket, authFetch, flash, onClose, onAdded }) {
  const [poolQ, setPoolQ] = useState('');
  const [pool, setPool] = useState([]);
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState([]);
  const [into, setInto] = useState(defaultBucket === '' ? '__new' : defaultBucket);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const [fromCsv, setFromCsv] = useState(false);

  const search = useCallback(async () => {
    setLoading(true);
    try {
      setPool(await searchPool(authFetch, poolQ, 40)); // C1-SEARCH
    } catch (e) { setPool([]); }
    setLoading(false);
  }, [authFetch, poolQ]);

  useEffect(() => { const t = setTimeout(search, 250); return () => clearTimeout(t); }, [search]);

  const onBench = new Set(members.map(m => m.candidate_id));
  function toggle(id) { setPicked(p => (p.indexOf(id) === -1 ? p.concat([id]) : p.filter(x => x !== id))); }

  async function add() {
    setBusy(true);
    try {
      let stageId = into;
      if (into === '__new') {
        const name = newName.trim();
        if (!name) { flash('Name the new bucket first.', true); setBusy(false); return; }
        const r = await authFetch('/api/benches/' + bench.id + '/buckets', {
          method: 'PUT',
          body: JSON.stringify({ buckets: buckets.map(b => ({ id: b.id, label: b.label, tone: b.tone }))
            .concat([{ label: name, tone: BUCKET_TONES[buckets.length % BUCKET_TONES.length] }]) }),
        });
        const made = (r.buckets || []).find(b => b.label === name);
        stageId = made ? made.id : null;
      }
      const r = await authFetch('/api/benches/' + bench.id + '/members', {
        method: 'POST', body: JSON.stringify({ candidate_ids: picked, stage_id: stageId === '' || stageId == null ? null : Number(stageId), source: 'manual' }),
      });
      const bits = [r.added + ' added'];
      if (r.already_there) bits.push(r.already_there + ' already on the bench');
      if (r.not_found) bits.push(r.not_found + ' could not be found');
      flash(bits.join(', ') + '.');
      onAdded(); onClose();
    } catch (e) { flash(e.message, true); }
    setBusy(false);
  }

  if (fromCsv) {
    // The platform import, fixed to this bench. The bucket chosen here carries over.
    const start = into === '__new' ? {} : { stage_id: into === '' || into == null ? null : Number(into) };
    return (
      <GtImportWizard entity="candidates" target={{ kind: 'bench', bench_id: bench.id, ...start }} lockTarget
        authFetch={authFetch} flash={flash} onClose={onClose} onDone={onAdded} />
    );
  }

  const bucketChoices = buckets.map(b => ({ value: b.id, label: b.label })).concat([{ value: '__new', label: '+ A new bucket' }]);

  return (
    <div className="gtc-modal-backdrop" onClick={() => !busy && onClose()}>
      <div className="gtc-modal gtsh-modal" onClick={e => e.stopPropagation()}>
        <div className="gtc-modal-head gtim-head">
          <span>Add people to {bench.name}</span>
          <div className="gtim-tabs" role="tablist">
            <button type="button" role="tab" aria-selected="true" className="gtim-tab gtim-tab--on">From your pool</button>
            <button type="button" role="tab" aria-selected="false" className="gtim-tab" disabled={busy} onClick={() => setFromCsv(true)}>From a CSV file</button>
          </div>
        </div>
        <div className="gtc-modal-body gtsh-body">
          <div className="gtbn-intorow">
            <div className="gtc-field gtbn-grow"><span>Into bucket</span>
              <GtSelect value={into} onChange={setInto} options={bucketChoices} />
            </div>
            {into === '__new' && (
              <label className="gtc-field gtbn-grow"><span>New bucket name</span>
                <input value={newName} maxLength={60} placeholder="e.g. Strong fit" onChange={e => setNewName(e.target.value)} />
              </label>
            )}
          </div>
          <input className="gtsh-search" autoFocus placeholder="Search the pool: skills, CV, notes, names… or describe who you need" value={poolQ} onChange={e => setPoolQ(e.target.value)} />
          {(function () {
            const free = pool.filter(c => !onBench.has(c.id)).map(c => c.id);
            if (!free.length) return null;
            const all = free.every(id => picked.indexOf(id) !== -1);
            return (
              <label className="gtbn-pickall">
                <input type="checkbox" className="gtpb-check" checked={all}
                  onChange={() => setPicked(p => (all ? p.filter(id => free.indexOf(id) === -1) : Array.from(new Set(p.concat(free)))))} />
                <span>{all ? 'All ' + free.length + ' shown selected' : 'Select all ' + free.length + ' shown'}</span>
                {picked.length > 0 && <b>{picked.length} picked</b>}
              </label>
            );
          })()}
          <div className="gtsh-picklist">
            {loading ? <div className="gtr-empty-sm">Searching…</div> : pool.length === 0 ? <div className="gtr-empty-sm">No candidates found.</div> : pool.map(c => {
              const already = onBench.has(c.id);
              const on = picked.indexOf(c.id) !== -1;
              return (
                <button type="button" key={c.id} disabled={already}
                  className={'gtsh-pickrow' + (already ? ' gtsh-pickrow--blocked' : '') + (on ? ' gtbn-pick--on' : '')}
                  onClick={() => toggle(c.id)}>
                  <input type="checkbox" className="gtpb-check" readOnly tabIndex={-1} checked={on || already} aria-hidden="true" />
                  <span className="gtsh-pickav" style={{ background: avatarColour(c.full_name) }}>{initials(c.full_name)}</span>
                  <span className="gtsh-pickbody">
                    <span className="gtsh-pickname">{c.full_name}{c.ref && <span className="gtsh-pickref">{c.ref}</span>}</span>
                    <span className="gtsh-picksub">{[c.current_title, c.current_company, c.location].filter(Boolean).join(' · ') || c.headline || '—'}</span>
                    {c.search_match && <SearchMatchLine m={c.search_match} />}
                  </span>
                  {already && <span className="gtsh-stage">on this bench</span>}
                </button>
              );
            })}
          </div>
        </div>
        <div className="gtc-modal-foot">
          <button className="gtc-btn-ghost" disabled={busy} onClick={onClose}>Cancel</button>
          <button className="gtc-primary" disabled={busy || !picked.length || (into === '__new' && !newName.trim())} onClick={add}>
            {busy ? 'Adding…' : picked.length ? 'Add ' + picked.length : 'Pick people to add'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Move to a live role ──────────────────────────────────────────────────
function ToRoleModal({ bench, memberIds, authFetch, flash, onClose, onDone }) {
  const [roles, setRoles] = useState(null);
  const [roleId, setRoleId] = useState('');
  const [stages, setStages] = useState([]);
  const [stageId, setStageId] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [noticeIds, setNoticeIds] = useState(null);
  const [bulkNoticeIds, setBulkNoticeIds] = useState(null);

  useEffect(() => {
    authFetch('/api/roles').then(r => {
      const list = Array.isArray(r) ? r : (r.roles || []);
      const open = list.filter(x => ['filled', 'closed'].indexOf(x.status) === -1);
      // Roles for this bench's client first: they are the likeliest home.
      open.sort((a, b) => (Number(b.client_id === bench.client_id) - Number(a.client_id === bench.client_id))
        || String(a.title).localeCompare(String(b.title)));
      setRoles(open);
    }).catch(() => setRoles([]));
  }, [authFetch, bench.client_id]);

  useEffect(() => {
    if (!roleId) { setStages([]); setStageId(''); return; }
    authFetch('/api/roles/' + roleId + '/stages').then(r => {
      const st = (r.stages || []).filter(s => s.outcome === 'open' && s.side !== 'client');
      setStages(st);
      const entry = st.find(s => s.key === 'sourced') || st.find(s => s.is_entry) || st[0];
      setStageId(entry ? entry.id : '');
    }).catch(() => setStages([]));
  }, [authFetch, roleId]);

  async function go() {
    setBusy(true);
    try {
      const r = await authFetch('/api/benches/' + bench.id + '/to-role', {
        method: 'POST', body: JSON.stringify({ member_ids: memberIds, role_id: Number(roleId), stage_id: stageId ? Number(stageId) : null }),
      });
      setResult(r);
      onDone();
      const ids = (r.notices || []).map(n => n.id);
      if (ids.length === 1) setNoticeIds(ids);
      else if (ids.length > 1) setBulkNoticeIds(ids);
    } catch (e) { flash(e.message, true); }
    setBusy(false);
  }

  const roleOpts = (roles || []).map(r => ({
    value: r.id, label: r.title + (r.client_name ? ' · ' + r.client_name : '') + (r.client_id && r.client_id === bench.client_id ? '  (this client)' : ''),
  }));

  return (
    <div className="gtc-modal-backdrop" onClick={() => !busy && onClose()}>
      <div className="gtc-modal" onClick={e => e.stopPropagation()}>
        <div className="gtc-modal-head">Move {memberIds.length === 1 ? '1 person' : memberIds.length + ' people'} to a role</div>
        <div className="gtc-modal-body">
          {result ? (
            <div className={'gtnt-banner ' + (result.failed ? 'gtnt-banner--amber' : 'gtnt-banner--green')}>
              <b>{result.added} added to {result.role.title}.</b>
              {result.already_on_role > 0 && ' ' + result.already_on_role + ' were already on it and were left where they are.'}
              {result.failed > 0 && ' ' + result.failed + ' could not be added.'}
              <ul className="gtnt-blocked">
                {result.results.map(x => (
                  <li key={x.member_id}><b>{x.name || 'Someone'}</b> {x.result === 'added' ? 'added at ' + (x.stage || 'the first stage')
                    : x.result === 'already_on_role' ? 'already on this role' + (x.stage ? ', at ' + x.stage : '')
                    : x.message}</li>
                ))}
              </ul>
              <p className="gtbn-help">They are still on this bench too.</p>
            </div>
          ) : (
            <>
              <p className="gtbn-help">They join the role's pipeline at the stage you pick, and stay on this bench. Nobody is emailed.</p>
              <div className="gtc-field"><span>Role</span>
                {roles === null ? <div className="gtr-empty-sm">Loading roles…</div> : roles.length === 0
                  ? <div className="gtr-empty-sm">There are no open roles.</div>
                  : <GtSelect value={roleId} onChange={setRoleId} options={roleOpts} placeholder="Choose a live role" />}
              </div>
              {roleId && (
                <div className="gtc-field"><span>Stage</span>
                  <GtSelect value={stageId} onChange={setStageId} placeholder="Choose a stage" options={stages.map(s => ({ value: s.id, label: s.label }))} />
                </div>
              )}
            </>
          )}
        </div>
        <div className="gtc-modal-foot">
          {result
            ? <button className="gtc-primary" onClick={onClose}>Done</button>
            : (
              <>
                <button className="gtc-btn-ghost" disabled={busy} onClick={onClose}>Cancel</button>
                <button className="gtc-primary" disabled={busy || !roleId} onClick={go}>{busy ? 'Moving…' : 'Move ' + memberIds.length}</button>
              </>
            )}
        </div>
        {noticeIds && <GtNoticeModal ids={noticeIds} authFetch={authFetch} onClose={() => setNoticeIds(null)} />}
        {bulkNoticeIds && <GtBulkNoticeModal ids={bulkNoticeIds} authFetch={authFetch} onClose={() => setBulkNoticeIds(null)} />}
      </div>
    </div>
  );
}
